import React, { Fragment, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Row, Col, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { abpnClient } from "../Services/AbpnClient";
import { AuthContext } from "./Auth/AuthProvider";
import CertificatesCard from "./Dashboard/Certificates/Components/CertificatesCard";
import { CertificationRequirements } from "./Dashboard/CertificationRequirements/Components/CertificationRequirements";
import CCEnrollmentSummary from "./Dashboard/ContinuingCertification/Components/CCEnrollmentSummary";
import CCEnrollmentCard from "./Dashboard/ContinuingCertification/Components/CCEnrollmentCard";
import { ExaminationsCard } from "./Dashboard/Exams/Components/ExaminationsCard";
import { CertificateStatus, CertificationGeneration } from "./Physician/Models/Certificate";
import Physician from "./Physician/Models/Physician";
import AbpnSpinner from "./Shared/Components/AbpnSpinner";
import HeaderWithTitle from "./Shared/Components/HeaderWithTitle";
import { CountryId } from "./Shared/Enums/CountryId";
import { ProfileContext } from "./Auth/ProfileProvider";
import NotificationManager from "./Shared/Components/SystemNotifications/NotificationManager";
import { AbccAssessment, AbccAssessmentModule } from "./Dashboard/ContinuingCertification/Models/CCAssessment";
import { deepCopy } from "./Shared/Utility/DeepCopy";
import CCBlockSummaryContainer from "./Dashboard/ContinuingCertification/Components/CCBlockSummaryContainer";
import { getDateWithOffset } from "./Shared/Utility/GetDateWithOffset";

export default function Home() {

    const [physician, setPhysician] = useState<Physician>(null);
    const [abccAssessments, setAbccAssessments] = useState<Array<AbccAssessment>>(null);
    const states = physician?.addresses.states;
    const authContext = useContext(AuthContext);
    const { user } = useContext(ProfileContext);
    const history = useHistory();

    const lifeTimeActiveCertificates = physician?.certificates.filter(cert => cert.certificationGeneration === CertificationGeneration.Lifetime && cert.certificateStatus == CertificateStatus.Active);

    useEffect(() => {
        const getPhysician = async () => {
            const physicianResult = await abpnClient.get<Physician>(`/api/physician`,authContext);
            setPhysician(physicianResult);
        }

        const updateLastLogin = async () => {
            await abpnClient.post<number>("/api/physician/UpdateLastLogin", user.physicianId, authContext);
        }

        const getAbccAssessments = async () => {
            const abccAssessmentsResult = await abpnClient.get<Array<AbccAssessment>>(
                `/api/physician/AbccAssessments`, 
                authContext
            );
        
            const assessments = abccAssessmentsResult.map(
                assessment => new AbccAssessment(assessment)
            );
        
            setAbccAssessments(assessments);
        };

        if (!user) return;
        if(user.physicianId != null && user.physicianId != "") updateLastLogin();
        if(user?.isOtherBoardPhysician){
            history.push('/OtherBoard');
        } else {
            getPhysician();
            getAbccAssessments();
        }
    }, [user]);

    const acceptAbccModuleAttestation = async (abccAssessmentModule: AbccAssessmentModule) => {
        console.log("abccAssessmentModuleId: ", abccAssessmentModule.abccAssessmentModuleId);
        console.log("abccAssessmentId: ", abccAssessmentModule.abccAssessmentId);
    
        try {
            await abpnClient.post<number>(
                "/api/physician/AcceptAbccModuleAttestation",
                abccAssessmentModule.abccAssessmentModuleId,
                authContext
            );
    
            const newAbccAssessments = deepCopy(abccAssessments);
    
            // Find the index of the assessment
            const abccAssessmentIndex = newAbccAssessments.findIndex(
                x => x.abccAssessmentId === abccAssessmentModule.abccAssessmentId
            );
    
            // Handle the case where the assessment is not found
            if (abccAssessmentIndex === -1) {
                console.error("Assessment not found for abccAssessmentId: ", abccAssessmentModule.abccAssessmentId);
                return;
            }
    
            // Find the index of the module
            const abccAssessmentModuleIndex = newAbccAssessments[abccAssessmentIndex].modules.findIndex(
                x => x.abccAssessmentModuleId === abccAssessmentModule.abccAssessmentModuleId
            );
    
            // Handle the case where the module is not found
            if (abccAssessmentModuleIndex === -1) {
                console.error("Module not found for abccAssessmentModuleId: ", abccAssessmentModule.abccAssessmentModuleId);
                return;
            }
    
            // Update the attestation date
            abccAssessmentModule.attestationDate = new Date().toLocaleDateString("en-US");
            newAbccAssessments[abccAssessmentIndex].modules[abccAssessmentModuleIndex] = abccAssessmentModule;
    
            setAbccAssessments(newAbccAssessments);
        } catch (error) {
            console.error("Error in acceptAbccModuleAttestation: ", error);
        }
    };
    


    if (!physician || !user) return <AbpnSpinner></AbpnSpinner>

    return (
        <Fragment>
            <HeaderWithTitle title={`Welcome, ${physician.fullName}`} subTitle={null} contactMessage={true}/>
            <NotificationManager physician={physician}/>
            <Row>
                <Col xs={12} md={12}>
                    <div className="d-flex justify-content-center">
                        <div>
                            <a className="pl-1 pp-font-size-smaller" href="https://abpn.org/continuing-certification/" target="_BLANK">
                                <img 
                                    width="600px" 
                                    className="img-fluid" 
                                    src="https://abpn.org/wp-content/uploads/2025/05/infographic-horizontal_webpage.jpg" 
                                    alt="ABPN Continuing Certification" 
                                />
                            </a>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={12}>
                    <React.Fragment>
                        <CCBlockSummaryContainer
                            physician={physician}
                            abccAssessments={abccAssessments} />
                    </React.Fragment>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={6}>
                    {physician.currentCCEnrollment?.ccBlocks.find((block) => {
                            var today = new Date();
                            var hasAudit = false;
                            var auditStarted = block.auditStart != null ? getDateWithOffset(block.auditStart) < getDateWithOffset(today.toDateString()) : false;
                            if(block.hasAudit && block.hasFailedAudit != true && block.hasPassedAudit != true && auditStarted) hasAudit = true;
                            return hasAudit;
                        }) &&
                        <React.Fragment>
                            <Card className="mb-4" border="pp-secondary-grey" bg="pp-alert-warning">
                                <Card.Body >
                                    <Card.Title className="mb-0 font-weight-bolder">                 
                                        <span className="text-uppercase">
                                            CONTINUING CERTIFICATION AUDIT
                                        </span>          
                                    </Card.Title>       
                                    <span className="mt-2 ml-4">Your Previous CC Block is being audited. Please contact your ABPN auditor for additional information.
                                    <OverlayTrigger
                                        placement={"right"}
                                        trigger="click"
                                        rootClose={true}
                                        overlay={<Tooltip id="failAuditBannerTooltip" bsPrefix="pp-tooltip">
                                            You have been randomly selected for an audit of your previous CC block.  If you have not yet received an email notification, please check your email spam folder or you should expect to get an email within the next few weeks.  
                                            You may visit the <a href="https://abpn.org/maintain-certification/abpn-diplomate-audits/">ABPN Diplomate Audits</a> page on our website to learn more about what to expect during the audit and what is required. 

                                            If you have any questions, please contact the ABPN office at <a href="mailto:credentals@abpn.org">credentals@abpn.org</a> or call (847)-229-6510.
                                        </Tooltip>}>
                                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                    </OverlayTrigger>
                                    </span>
                                </Card.Body>
                            </Card>
                        </React.Fragment>
                    }
                    {physician.currentCCEnrollment != null ?
                        <React.Fragment>
                            <CCEnrollmentCard
                                currentEnrollment={physician.currentCCEnrollment}
                                physician={physician}
                                states={states.filter(x => x.countryId === CountryId.Usa || x.countryId === CountryId.Canada)}
                                updatePhysician={(physician: Physician) => setPhysician(physician)} />
                        </React.Fragment>
                        :
                        <React.Fragment>
                            <CertificationRequirements
                                physician={physician}
                                states={states.filter(x => x.countryId === CountryId.Usa)}
                                updatePhysician={(physician: Physician) => setPhysician(physician)} />
                        </React.Fragment>}
                </Col>
                <Col xs={12} md={6}>
                    {((physician?.currentCCEnrollment?.activeEnrollmentCertificates && physician?.currentCCEnrollment?.activeEnrollmentCertificates.length > 0)
                        || (lifeTimeActiveCertificates && lifeTimeActiveCertificates.length > 0))
                        && <CCEnrollmentSummary physician={physician} abccAssessments={abccAssessments} acceptAbccModuleAttestation={acceptAbccModuleAttestation} appliedArticleWaivers={physician.currentCCEnrollment?.currentCCBlock?.ccAppliedArticleWaivers} />}
                    <ExaminationsCard />
                    <CertificatesCard physician={physician} updatePhysician={(physician: Physician) => setPhysician(physician)} />
                </Col>
            </Row>
        </Fragment >
    );
}